import { useEffect, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useIsInstalledPWA } from '../../utils/pwaDetection';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { analytics } from '../../hooks/useAnalytics';

const SHOW_DELAY_MS = 2000;

/**
 * Bottom-sheet prompt that invites the user to add the app to their home
 * screen. Shown only to authenticated users who haven't installed the PWA yet.
 *
 * - Android / Desktop Chrome: triggers the native browser install dialog.
 * - iOS Safari: shows step-by-step manual instructions.
 *
 * Suppressed for 3 days after the user dismisses it.
 */
export function InstallPWAPrompt() {
  const user                                          = useAuthStore((s) => s.user);
  const { isInstalled, platform }                     = useIsInstalledPWA();
  const { canNativeInstall, shouldShowPrompt,
          triggerInstall, dismiss }                   = useInstallPrompt();
  const [visible, setVisible]                         = useState(false);

  const isIOS     = platform === 'ios';
  const isMobile  = platform === 'ios' || platform === 'android';

  // Only show on mobile, only when logged in, not already installed.
  const eligible = user && isMobile && !isInstalled && shouldShowPrompt;
  // On Android we need the deferred event; on iOS we always show instructions.
  const hasContent = isIOS || canNativeInstall;

  useEffect(() => {
    if (!eligible || !hasContent) return;
    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [eligible, hasContent]);

  function handleDismiss() {
    setVisible(false);
    dismiss();
  }

  async function handleInstall() {
    await triggerInstall();
    analytics.trackAppInstalled();
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleDismiss}
        style={{
          position:        'fixed',
          inset:           0,
          background:      'rgba(26,28,30,0.4)',
          zIndex:          200,
          animation:       'fadeIn 0.2s ease',
        }}
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add to Home Screen"
        style={{
          position:        'fixed',
          bottom:          0,
          left:            0,
          right:           0,
          background:      '#ffffff',
          borderRadius:    '20px 20px 0 0',
          padding:         '12px 24px max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 40px)',
          zIndex:          201,
          boxShadow:       '0 -8px 40px -4px rgba(43,52,55,0.16)',
          animation:       'slideUp 0.28s cubic-bezier(0.34,1.3,0.64,1)',
        }}
      >
        {/* Drag handle */}
        <div style={{
          width:        40,
          height:       4,
          borderRadius: 2,
          background:   '#e2e8f0',
          margin:       '0 auto 20px',
        }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width:        48,
              height:       48,
              borderRadius: 12,
              background:   'var(--primary-container)',
              display:      'flex',
              alignItems:   'center',
              justifyContent: 'center',
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 28, color: 'var(--primary)' }}>
                local_parking
              </span>
            </div>
            <div>
              <div style={{ fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: 16, color: 'var(--on-surface)' }}>
                Add LotNGo to Home Screen
              </div>
              <div style={{ fontSize: 13, color: 'var(--on-surface-3)', marginTop: 2 }}>
                Faster access + parking alerts
              </div>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            style={{
              background:   'none',
              border:       'none',
              cursor:       'pointer',
              padding:      4,
              color:        'var(--on-surface-3)',
              display:      'flex',
              alignItems:   'center',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 22 }}>close</span>
          </button>
        </div>

        {/* Android / Desktop: native install button */}
        {!isIOS && canNativeInstall && (
          <button
            onClick={handleInstall}
            style={{
              width:        '100%',
              padding:      '14px 0',
              borderRadius: 'var(--radius-btn)',
              background:   'var(--primary)',
              color:        'var(--on-primary)',
              border:       'none',
              cursor:       'pointer',
              fontFamily:   'var(--inter)',
              fontWeight:   600,
              fontSize:     15,
              display:      'flex',
              alignItems:   'center',
              justifyContent: 'center',
              gap:          8,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>add_to_home_screen</span>
            Install App
          </button>
        )}

        {/* iOS: manual step-by-step instructions */}
        {isIOS && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <IOSStep
              icon="ios_share"
              text={<>Tap the <strong>Share</strong> button at the bottom of Safari</>}
            />
            <IOSStep
              icon="add_box"
              text={<>Scroll down and tap <strong>Add to Home Screen</strong></>}
            />
            <IOSStep
              icon="check_circle"
              text={<>Tap <strong>Add</strong> to confirm</>}
            />

            <button
              onClick={handleDismiss}
              style={{
                marginTop:    8,
                width:        '100%',
                padding:      '13px 0',
                borderRadius: 'var(--radius-btn)',
                background:   'var(--surface-low)',
                color:        'var(--on-surface-2)',
                border:       'none',
                cursor:       'pointer',
                fontFamily:   'var(--inter)',
                fontWeight:   600,
                fontSize:     15,
              }}
            >
              Maybe later
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
      `}</style>
    </>
  );
}

function IOSStep({ icon, text }: { icon: string; text: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{
        width:          40,
        height:         40,
        minWidth:       40,
        borderRadius:   10,
        background:     'var(--primary-container)',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 22, color: 'var(--primary)' }}>
          {icon}
        </span>
      </div>
      <span style={{ fontSize: 14, color: 'var(--on-surface-2)', lineHeight: 1.4 }}>
        {text}
      </span>
    </div>
  );
}
